import { addDays, formatClientDate, subDays } from '../../util/datetime-fns';
import type { Month, Duration } from './DepartureByMonth';

type PrePackageSearchType = 'flexible' | 'fixed';

interface PrePackageState {
    originAirport?: string;
    destinationId?: string;
    departureDate?: Date;
    returnDate?: Date;
    flexibleDeparture?: number;
    travelMonths?: Month[];
    duration?: Duration;
    searchType?: PrePackageSearchType;
}

export const dateRangeFor = (baseDate: Date, flexibility: number): string => {
    const startDate = subDays(flexibility, baseDate);
    const endDate = addDays(flexibility, baseDate);
    return `${formatClientDate(startDate)}-${formatClientDate(endDate)}`;
};

export const durationFor = (duration: Duration): string => `${duration.from}-${duration.to}`;

export const monthFor = (month: Month): string => `${`0${month.month}`.slice(-2)}.${month.year}`;

const buildFixedDepartureParams = (state: PrePackageState): (string | null)[] => [
    state.departureDate && state.flexibleDeparture != null
        ? `departureDate=${dateRangeFor(state.departureDate, state.flexibleDeparture)}`
        : null,
    state.returnDate && state.flexibleDeparture != null ? `returnDate=${dateRangeFor(state.returnDate, state.flexibleDeparture)}` : null,
];

const buildFlexibleDepartureParams = (state: PrePackageState): string[] => {
    const allMonths = Array.from({ length: 12 }, (_, i) => {
        const date = new Date();
        date.setMonth(date.getMonth() + i);
        return `${date.getMonth() + 1}-${date.getFullYear()}`;
    });

    const selected = (state.travelMonths ?? []).map((m) => `${m.month}-${m.year}`);
    const isAllMonthsSelected = selected.length === 12 && allMonths.every((key) => selected.includes(key));

    const params: string[] = [];

    if (!isAllMonthsSelected) {
        params.push(...(state.travelMonths?.map((month) => `travelMonths=${monthFor(month)}`) ?? []));
    }

    if (state.duration) {
        params.push(`duration=${durationFor(state.duration)}`);
    }

    return params;
};

export const buildParams = (state: PrePackageState): string => {
    const params: (string | null)[] = [];

    if (state.originAirport) {
        params.push(`from=${state.originAirport}`);
    }

    if (state.destinationId) {
        params.push(`to=${state.destinationId}`);
    }

    if (state.searchType === 'flexible') {
        params.push(...buildFlexibleDepartureParams(state));
    } else {
        params.push(...buildFixedDepartureParams(state));
    }

    return params.filter((param): param is string => param !== null).join('&');
};
